import { useNavigate } from 'react-router-dom';
import React, { useCallback, useState } from 'react';

import Tippy from '@tippyjs/react';
import { useFormik } from 'formik';
import { useRecoilValue } from 'recoil';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import useDebounceApi from 'lib/hooks/useDebounceApi';
import { usePayers } from 'lib/hooks/queries/payers/usePayers';
import { useSupervisor } from 'lib/hooks/queries/supervisor/useSupervisor';

import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import Select from 'components/shared/Select/Select';
import adminState from 'components/admin/lib/adminState';
import { showAlert } from 'components/shared/Alert/Alert';

import { initialValues } from '../lib/initials';
import {
  extractNewObjectIds,
  getChanges,
  processData,
  searchProviderOptions
} from '../lib/helpers';

import { io, optionify } from '/lib/helpers/utility';
import { useServices } from '/lib/hooks/queries/services/useServices';
import { submitSupervision, updateSupervision } from '/api/Supervision';

const SupervisorSettings = ({ member, hideModal, showModal }) => {
  const admin = useRecoilValue(adminState.current_admin);
  const [mode, setMode] = useState({ update: false });
  const navigate = useNavigate();

  const onSuccess = (response) => {
    if (!io(response)) return response;

    setMode({ update: true });
    formik.setValues(initialValues(response));
  };
  const sendPracticeIdIfCurrentUserAdmin = !!admin?.user_id &&
    admin?.logged_in &&
    !!admin?.email && {
      params: {
        practiceId: member?.practice_id
      }
    };

  const { data: payers } = usePayers({
    ...sendPracticeIdIfCurrentUserAdmin,
    options: { select: (response) => optionify(response.payers, 'name', 'id') }
  });

  const useSupervisorParams = {
    ...sendPracticeIdIfCurrentUserAdmin?.params,
    supervisorIds: [member.user_id],
    withRelations: { user: true, supervisor: true, procedure: true, payer: true }
  };

  const { data } = useSupervisor({
    params: useSupervisorParams,
    options: {
      onSuccess: (response) => onSuccess(response),
      select: ({ data }) => processData(data),
      refetchOnMount: true
    },
    dependencies: [member]
  });

  const fetchProviders = useCallback(
    (searchTerm, resolve) =>
      searchProviderOptions(navigate, sendPracticeIdIfCurrentUserAdmin?.params || {}, searchTerm)
        .then(resolve)
        .catch(() => resolve([])),
    [navigate, sendPracticeIdIfCurrentUserAdmin?.params]
  );

  const { debouncedFn: debouncedLoadProviders } = useDebounceApi({ apiFn: fetchProviders });

  const loadProviders = (searchTerm) =>
    new Promise((resolve) => debouncedLoadProviders(searchTerm, resolve));

  const { data: procedures } = useServices({
    params: {
      ...sendPracticeIdIfCurrentUserAdmin.params,
      offset: 0
    },
    options: {
      select: (response) => optionify(response.services, 'name', 'id')
    },
    dependencies: [member]
  });

  const queryClient = useQueryClient();

  const handleSupervisionSuccess = (message) => () => {
    queryClient.invalidateQueries(['supervisorData']);
    showAlert({ title: message, color: 'success' });
  };

  const updateSupervisionMutation = useMutation((data) => updateSupervision(navigate, data), {
    onSuccess: handleSupervisionSuccess('Supervisor settings updated')
  });

  const createSupervisionMutation = useMutation((data) => submitSupervision(navigate, data), {
    onSuccess: handleSupervisionSuccess('Supervisor settings created')
  });

  const formik = useFormik({
    initialValues: initialValues({ ...data, supervisorId: member?.user_id }),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      if (mode.update) {
        const changes = getChanges(formik, values);

        updateSupervisionMutation.mutate({
          ...changes,
          supervisorId: member.user_id,
          practiceId: member?.practice_id
        });
      } else {
        const supervisorId = member?.user_id;
        const finalizedData = extractNewObjectIds({ ...values, supervisorId });

        createSupervisionMutation.mutate({ ...finalizedData, practiceId: member?.practice_id });
      }

      hideModal();
      setSubmitting(false);
    }
  });

  const Label = ({ label, tippyMessage }) => (
    <div className="flex gap-2">
      <div>{label}</div>
      <Tippy content={tippyMessage} className="tippy-dark">
        <div>
          <Icon icon="info-filled" color="neutral" />
        </div>
      </Tippy>
    </div>
  );

  return (
    showModal && (
      <Modal
        handleOpen={showModal}
        handleClose={hideModal}
        title={member?.f_name ? `Supervisor: ${member.f_name}` : ''}
        className="w-[720px] sm:w-full"
        footer={
          <div className="flex w-full justify-between">
            <Button text="Cancel" variant="tertiary" onClick={hideModal} size="small" />

            {mode?.update ? (
              <Button
                text="Update"
                isLoading={updateSupervisionMutation?.isLoading}
                isDisabled={updateSupervisionMutation?.isLoading}
                onClick={formik.submitForm}
                size="small"
              />
            ) : (
              <Button
                text="Create"
                isLoading={createSupervisionMutation?.isLoading}
                isDisabled={createSupervisionMutation?.isLoading}
                onClick={formik.submitForm}
                size="small"
              />
            )}
          </div>
        }>
        <div className="grid gap-y-4">
          <Select
            isMulti
            isAsync
            defaultOptions
            inputId="providers-select"
            loadOptions={loadProviders}
            label={Label({
              label: 'Providers',
              tippyMessage: `Please note that any provider entered below will require supervisory signature by ${member.f_name} ${member.l_name}`
            })}
            placeholder="Type to search providers"
            value={formik?.values?.users || []}
            onChange={(option) => formik.setFieldValue('users', option)}
          />

          <Select
            isMulti
            inputId="payers-select"
            options={payers || []}
            label={Label({
              label: 'Payers',
              tippyMessage: `Supervisory signature is needed by ${member.f_name} ${member.l_name} only if the patient uses one of the insurance companies listed below`
            })}
            placeholder="Type and select payers"
            value={formik?.values?.payers || []}
            onChange={(option) => formik.setFieldValue('payers', option)}
          />

          <Select
            isMulti
            inputId="procedures-select"
            options={procedures || []}
            label={Label({
              label: 'Services',
              tippyMessage: `Supervisory signature is needed by ${member.f_name} ${member.l_name} only when one of the procedures listed below is rendered AND the patient uses one of the payers selected (if applicable)`
            })}
            placeholder="Type and select services"
            value={formik?.values?.procedures || []}
            onChange={(option) => formik.setFieldValue('procedures', option)}
          />
        </div>
      </Modal>
    )
  );
};

export default SupervisorSettings;
